/**
 * Repository picker — choose which GitHub repo a project's tests are delivered to.
 *
 * Mirrors the Claude Code "Select repo…" control. The states it walks through:
 *
 *   No App on the deployment   → nothing to connect; hidden.
 *   App present, not connected → "Connect GitHub" (opens the install flow).
 *   Connected, no repo picked  → "Select repo…" (searchable list of the
 *                                 installation's repos).
 *   Repo chosen                → a chip showing owner/repo; click to change.
 *
 * No token is ever entered here — connecting means installing the GitHub App,
 * and the backend mints a short-lived credential per delivery. See
 * github_app_service on the backend.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
  InputAdornment, List, ListItemButton, ListItemText,
  TextField, Tooltip, Typography,
} from '@mui/material';
import {
  GitHub as GitHubIcon, Search as SearchIcon, Lock as LockIcon,
  Public as PublicIcon, Check as CheckIcon, Add as AddIcon,
  Refresh as RefreshIcon, Person as PersonIcon,
} from '@mui/icons-material';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8003/api/v1';

interface Repo {
  full_name: string;
  url: string;
  private: boolean;
  default_branch: string;
}

interface Installation {
  id: number | string;
  account: string;
  target_type?: string;
}

interface Props {
  projectId?: string | number | null;
}

const get = async (path: string) => {
  const r = await fetch(`${API_URL}${path}`, { credentials: 'include' });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
  return r.json();
};
const post = async (path: string, body: unknown) => {
  const r = await fetch(`${API_URL}${path}`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
  return r.json();
};

const RepositoryPicker: React.FC<Props> = ({ projectId }) => {
  const [appConfigured, setAppConfigured] = useState<boolean | null>(null);
  const [binding, setBinding] = useState<{ repo_url?: string } | null>(null);
  const [connected, setConnected] = useState(false); // App installed for this project
  const [open, setOpen] = useState(false);
  const [repos, setRepos] = useState<Repo[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Account chooser — pick an existing App installation to bind (avoids the
  // fragile install-callback round-trip when the App is already installed).
  const [accountOpen, setAccountOpen] = useState(false);
  const [installations, setInstallations] = useState<Installation[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [binding_, setBinding_] = useState<string | null>(null); // id being bound

  // Is there an App on this deployment at all?
  useEffect(() => {
    get('/projects/git/github/status')
      .then((d) => setAppConfigured(!!d.configured))
      .catch(() => setAppConfigured(false));
  }, []);

  // Current binding + whether this project has installed the App.
  const refreshBinding = useCallback(() => {
    if (!projectId) return;
    get(`/projects/${projectId}/git/binding`)
      .then((d) => { setBinding(d.connected ? d : null); })
      .catch(() => setBinding(null));
    get(`/projects/${projectId}/git/github/repos`)
      .then(() => setConnected(true))
      .catch(() => setConnected(false));
  }, [projectId]);

  useEffect(() => { refreshBinding(); }, [refreshBinding]);

  const repoLabel = useMemo(() => {
    if (!binding?.repo_url) return null;
    try { return new URL(binding.repo_url).pathname.replace(/^\//, ''); }
    catch { return binding.repo_url; }
  }, [binding]);

  // Open GitHub's install page in a new tab (first-time install, or to add the
  // App to another account). Binding still happens by picking the account here
  // afterwards, so we don't depend on the callback firing.
  const openInstallPage = async () => {
    if (!projectId) return;
    try {
      const { install_url } = await get(`/projects/${projectId}/git/github/install-url`);
      window.open(install_url, '_blank', 'noopener');
    } catch (e: any) { setError(e.message); }
  };

  // "Connect GitHub" → if the App is already installed somewhere, let the user
  // pick that account and bind it directly. Only send them to GitHub to install
  // when there's no installation at all.
  const startConnect = async () => {
    if (!projectId) return;
    setError(null);
    setLoadingAccounts(true);
    try {
      const { installations } = await get('/projects/git/github/installations');
      const list: Installation[] = installations || [];
      setInstallations(list);
      if (list.length === 0) {
        await openInstallPage();
      } else {
        setAccountOpen(true);
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoadingAccounts(false);
    }
  };

  // Bind a chosen installation to this project, then jump straight to the repo
  // picker for that account.
  const bindInstallation = async (inst: Installation) => {
    if (!projectId) return;
    setBinding_(String(inst.id));
    setError(null);
    try {
      await post(`/projects/${projectId}/git/github/bind-installation`, {
        installation_id: String(inst.id),
      });
      setConnected(true);
      setAccountOpen(false);
      await openPicker();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBinding_(null);
    }
  };

  // The user just installed on a new account in the other tab — re-fetch so it
  // shows up in the chooser.
  const refreshInstallations = async () => {
    setError(null);
    setLoadingAccounts(true);
    try {
      const { installations } = await get('/projects/git/github/installations');
      setInstallations(installations || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const loadRepos = async () => {
    setError(null); setLoadingRepos(true);
    try {
      const { repos } = await get(`/projects/${projectId}/git/github/repos`);
      setRepos(repos);
    } catch (e: any) {
      setError(e.message);
    } finally { setLoadingRepos(false); }
  };

  const openPicker = async () => {
    setOpen(true);
    await loadRepos();
  };

  // Open GitHub's "configure installation" page so the user can grant the App
  // access to another repository (existing or newly created), then Refresh.
  const openManageRepos = async () => {
    try {
      const { manage_url } = await get(`/projects/${projectId}/git/github/manage-url`);
      window.open(manage_url, '_blank', 'noopener');
    } catch (e: any) { setError(e.message); }
  };

  const pick = async (repo: Repo) => {
    try {
      await post(`/projects/${projectId}/git/github/select-repo`, {
        repo_url: repo.url, default_branch: repo.default_branch,
      });
      setBinding({ repo_url: repo.url });
      setOpen(false);
    } catch (e: any) { setError(e.message); }
  };

  const filtered = useMemo(
    () => repos.filter((r) => r.full_name.toLowerCase().includes(query.toLowerCase())),
    [repos, query],
  );

  // Nothing to show if no App is configured or no project is selected.
  if (appConfigured === false || !projectId) return null;
  if (appConfigured === null) return null; // still checking

  return (
    <>
      {binding?.repo_url ? (
        <Tooltip title="Tests are delivered here as pull requests. Click to change.">
          <Chip
            icon={<GitHubIcon sx={{ fontSize: '1rem !important' }} />}
            label={repoLabel}
            size="small"
            variant="outlined"
            onClick={openPicker}
            sx={{ ml: 1, maxWidth: 220, fontFamily: 'monospace', fontSize: '0.72rem' }}
          />
        </Tooltip>
      ) : connected ? (
        <Button size="small" variant="outlined" startIcon={<GitHubIcon />}
          onClick={openPicker} sx={{ ml: 1, textTransform: 'none' }}>
          Select repo…
        </Button>
      ) : (
        <Button size="small" variant="outlined"
          startIcon={loadingAccounts ? <CircularProgress size={14} /> : <GitHubIcon />}
          disabled={loadingAccounts}
          onClick={startConnect} sx={{ ml: 1, textTransform: 'none' }}>
          Connect GitHub
        </Button>
      )}

      {/* Account chooser — pick an existing App installation to bind. */}
      <Dialog open={accountOpen} onClose={() => setAccountOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GitHubIcon fontSize="small" />
            <span>Connect an account</span>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Pick the account or org where the GitHub App is installed. Its
            repositories become available to deliver tests to.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          {error && <Typography variant="body2" color="error" sx={{ py: 1 }}>{error}</Typography>}
          {loadingAccounts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
          ) : (
            <List dense sx={{ maxHeight: 320, overflow: 'auto' }}>
              {installations.map((inst) => (
                <ListItemButton
                  key={String(inst.id)}
                  onClick={() => bindInstallation(inst)}
                  disabled={binding_ !== null}
                >
                  <PersonIcon sx={{ fontSize: '1rem', mr: 1, color: 'text.disabled' }} />
                  <ListItemText
                    primary={inst.account}
                    primaryTypographyProps={{ fontFamily: 'monospace', fontSize: '0.82rem' }}
                    secondary={inst.target_type === 'Organization' ? 'Organization' : 'User'}
                  />
                  {binding_ === String(inst.id) && <CircularProgress size={16} />}
                </ListItemButton>
              ))}
              <ListItemButton onClick={openInstallPage} disabled={binding_ !== null}>
                <AddIcon sx={{ fontSize: '1rem', mr: 1, color: 'primary.main' }} />
                <ListItemText
                  primary="Install on another account…"
                  primaryTypographyProps={{ fontSize: '0.82rem', color: 'primary.main' }}
                  secondary="Opens GitHub in a new tab"
                />
              </ListItemButton>
              <ListItemButton onClick={refreshInstallations} disabled={binding_ !== null}>
                <RefreshIcon sx={{ fontSize: '1rem', mr: 1, color: 'text.disabled' }} />
                <ListItemText
                  primary="Refresh"
                  primaryTypographyProps={{ fontSize: '0.82rem' }}
                  secondary="After installing on a new account"
                />
              </ListItemButton>
            </List>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GitHubIcon fontSize="small" />
            <span>Deliver tests to…</span>
          </Box>
          <Typography variant="caption" color="text.secondary">
            The agent opens a pull request here. It never merges — your team does.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <TextField
            fullWidth size="small" autoFocus placeholder="Search repositories…"
            value={query} onChange={(e) => setQuery(e.target.value)}
            InputProps={{ startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ) }}
            sx={{ mb: 1 }}
          />
          {error && <Typography variant="body2" color="error" sx={{ py: 1 }}>{error}</Typography>}
          {loadingRepos ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
          ) : filtered.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              {repos.length === 0 ? 'No repositories in this installation.' : 'No match.'}
            </Typography>
          ) : (
            <List dense sx={{ maxHeight: 320, overflow: 'auto' }}>
              {filtered.map((r) => {
                const selected = binding?.repo_url === r.url;
                return (
                  <ListItemButton key={r.full_name} onClick={() => pick(r)} selected={selected}>
                    {r.private ? <LockIcon sx={{ fontSize: '0.9rem', mr: 1, color: 'text.disabled' }} />
                               : <PublicIcon sx={{ fontSize: '0.9rem', mr: 1, color: 'text.disabled' }} />}
                    <ListItemText
                      primary={r.full_name}
                      primaryTypographyProps={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                      secondary={r.default_branch}
                    />
                    {selected && <CheckIcon sx={{ fontSize: '1rem', color: 'success.main' }} />}
                  </ListItemButton>
                );
              })}
            </List>
          )}

          {/* Don't see the repo you want? The installation only lists repos it
              was granted. Add another on GitHub, then refresh. */}
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 1, pt: 1 }}>
            <ListItemButton onClick={openManageRepos} sx={{ borderRadius: 1 }}>
              <AddIcon sx={{ fontSize: '1rem', mr: 1, color: 'primary.main' }} />
              <ListItemText
                primary="Add a repository on GitHub…"
                primaryTypographyProps={{ fontSize: '0.8rem', color: 'primary.main' }}
                secondary="Grant the app access to another repo, then Refresh"
              />
            </ListItemButton>
            <ListItemButton onClick={loadRepos} disabled={loadingRepos} sx={{ borderRadius: 1 }}>
              <RefreshIcon sx={{ fontSize: '1rem', mr: 1, color: 'text.disabled' }} />
              <ListItemText
                primary="Refresh"
                primaryTypographyProps={{ fontSize: '0.8rem' }}
                secondary="Reload after adding a repo on GitHub"
              />
            </ListItemButton>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RepositoryPicker;
